import { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { Check } from "@phosphor-icons/react"
import { Chip } from "@/components/ui/chip"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useUserStore } from "@/lib/fetch-users"
import { convertLanguageToISOCode } from "@/lib/language-to-iso-code"
import { cn } from "@/lib/utils"
import { useIsAutoTranslationEnabled } from "@/modules/chats/hooks/useIsAutoTranslationEnabled"
import { useSetChatAutoTranslationSettingMutation } from "@/modules/chats/services/useSetChatAutoTranslationSettingMutation"
import { ChatThread } from "@/modules/chats/types/chat-thread"
import { Language, LanguageValues } from "@/types/__generated__/union-types"

interface ChatAutoTranslationHeaderProps {
  thread: ChatThread
}

export const ChatAutoTranslationHeader = ({ thread }: ChatAutoTranslationHeaderProps) => {
  const { t } = useTranslation()

  const isEnabled = useIsAutoTranslationEnabled()
  const loginUser = useUserStore((state) => state.loginUser)

  const [setChatAutoTranslationSetting, isSettingInFlight] = useSetChatAutoTranslationSettingMutation()

  const backendSetting = thread.autoTranslationSetting
  const defaultTargetLanguage: Language = loginUser?.roleData?.preferredLanguage ?? thread.language

  const isLiveTranslationEnabled = backendSetting?.isLiveTranslationEnabled ?? false
  const targetLanguage: Language = backendSetting?.targetAgentLanguage ?? defaultTargetLanguage

  const persistSetting = useCallback(
    (nextIsLiveTranslationEnabled: boolean, nextTargetLanguage: Language) => {
      setChatAutoTranslationSetting({
        variables: {
          input: {
            appointmentId: thread.appointmentId,
            isLiveTranslationEnabled: nextIsLiveTranslationEnabled,
            targetAgentLanguage: nextTargetLanguage,
          },
        },
      })
    },
    [setChatAutoTranslationSetting, thread.appointmentId],
  )

  const handleLiveTranslationToggle = (checked: boolean) => {
    persistSetting(checked, targetLanguage)
  }

  const handleLanguageChange = (value: string) => {
    const nextTargetLanguage = LanguageValues.find((language) => language === value)
    if (!nextTargetLanguage) return

    persistSetting(isLiveTranslationEnabled, nextTargetLanguage)
  }

  if (!isEnabled) return null

  return (
    <div className="border-b-border flex items-center justify-end border-b px-4 py-2">
      <div className="flex items-center gap-2">
        <Chip
          checked={isLiveTranslationEnabled}
          onCheckedChange={handleLiveTranslationToggle}
          disabled={isSettingInFlight}
          className="h-8 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50">
          <div className="flex items-center gap-1">
            <Check
              className={cn("overflow-hidden transition-all duration-200", isLiveTranslationEnabled ? "w-5" : "w-0")}
              size={18}
            />
            <span className="px-1">{t("chat.autoTranslation.liveTranslation")}</span>
          </div>
        </Chip>
        <div className="bg-border h-5 w-px" />
        <Select value={targetLanguage} onValueChange={handleLanguageChange} disabled={isSettingInFlight}>
          <SelectTrigger
            className={cn(
              "bg-surface h-8 w-auto cursor-pointer gap-2 border px-3 disabled:cursor-not-allowed disabled:opacity-50",
            )}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {LanguageValues.map((language) => (
              <SelectItem key={language} value={language}>
                {convertLanguageToISOCode(language)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}
